import type { AccessContext } from "../types/api";
import type {
  DwbCard,
  DwbSelectionCardInput,
  DwbWeekAdapter,
  DwbWeekRun,
  DwbWeekSource,
} from "./dwbApi";

export const DWB_MANAGE_PERMISSION = "dwb_manage";
export const DWB_SELECTOR_REPOSITORY = "De-Boer-en-van-der-Weijde-Infra-B-V/dbvdw-dwb";
export const DWB_SELECTOR_VERSION = "selection-v1";

export const RUN_STATUS_LABELS: Record<string, string> = {
  draft: "Concept",
  selected: "Geselecteerd",
  reviewed: "Beoordeeld",
  ready: "Gereed",
  submitting: "Wordt ingediend",
  submitted: "Ingediend",
  failed: "Mislukt",
};

const COMMIT_RE = /^[0-9a-f]{40}$/;
const SEED_RE = /^[0-9]{1,20}$/;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isoParts(date: Date): { year: number; week: number } {
  const local = new Date(date);
  const utc = new Date(
    Date.UTC(local.getFullYear(), local.getMonth(), local.getDate()),
  );
  const weekday = utc.getUTCDay() || 7;
  utc.setUTCDate(utc.getUTCDate() + 4 - weekday);
  const yearStart = new Date(Date.UTC(utc.getUTCFullYear(), 0, 1));
  const week = Math.ceil(
    (((utc.getTime() - yearStart.getTime()) / 86_400_000) + 1) / 7,
  );
  return { year: utc.getUTCFullYear(), week };
}

export function currentIsoWeek(date = new Date()): string {
  const { year, week } = isoParts(date);
  return `${year}-W${String(week).padStart(2, "0")}`;
}

export function isoWeekMonday(isoWeek: string): Date {
  const match = /^(\d{4})-W(\d{2})$/.exec(String(isoWeek ?? ""));
  if (!match) throw new Error("Ongeldige ISO-week");
  const year = Number(match[1]);
  const week = Number(match[2]);
  const jan4 = new Date(Date.UTC(year, 0, 4));
  const jan4Weekday = jan4.getUTCDay() || 7;
  const monday = new Date(jan4);
  monday.setUTCDate(jan4.getUTCDate() - jan4Weekday + 1 + (week - 1) * 7);
  if (
    currentIsoWeek(
      new Date(
        monday.getUTCFullYear(),
        monday.getUTCMonth(),
        monday.getUTCDate(),
      ),
    ) !==
      isoWeek
  ) {
    throw new Error("Ongeldige ISO-week");
  }
  return monday;
}

export function shiftIsoWeek(isoWeek: string, deltaWeeks: number): string {
  const monday = isoWeekMonday(isoWeek);
  monday.setUTCDate(monday.getUTCDate() + Number(deltaWeeks) * 7);
  return currentIsoWeek(
    new Date(
      monday.getUTCFullYear(),
      monday.getUTCMonth(),
      monday.getUTCDate(),
    ),
  );
}

export function canManageDwb(
  context: AccessContext,
  projectId: string,
): boolean {
  const project = Array.isArray(context?.projects)
    ? context.projects.find((item) => item?.project_id === projectId)
    : null;
  return Array.isArray(project?.permissions) &&
    project.permissions.includes(DWB_MANAGE_PERMISSION);
}

function canonicalize(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(canonicalize);
  if (!value || typeof value !== "object") return value;
  const record = value as Record<string, unknown>;
  return Object.fromEntries(
    Object.keys(record).sort().map((key) => [key, canonicalize(record[key])]),
  );
}

export function stableJsonFingerprint(value: unknown): string {
  return JSON.stringify(canonicalize(value));
}

export function sourceRevisionFingerprint(
  source: DwbWeekSource | null | undefined,
): string {
  const days = Array.isArray(source?.days) ? source.days : [];
  return JSON.stringify(
    [...days]
      .map((day) => ({
        date: day?.date ?? null,
        day_report_id: day?.day_report_id ?? null,
        revision: day?.revision ?? null,
        updated_at: day?.updated_at ?? null,
      }))
      .sort((left, right) => String(left.date).localeCompare(String(right.date))),
  );
}

export function runSourceIsStale(
  run: DwbWeekRun | null | undefined,
  currentSource: DwbWeekSource | null | undefined,
): boolean {
  if (!run?.source_snapshot || !currentSource) return false;
  return sourceRevisionFingerprint(run.source_snapshot) !==
    sourceRevisionFingerprint(currentSource);
}

export function runAdapterIsStale(
  run: DwbWeekRun | null | undefined,
  currentAdapter: DwbWeekAdapter | null | undefined,
): boolean {
  if (!run?.adapter_snapshot || !currentAdapter) return false;
  return stableJsonFingerprint(run.adapter_snapshot) !==
    stableJsonFingerprint(currentAdapter);
}

export interface DwbReviewSummary {
  total: number;
  pending: number;
  approved: number;
  rejected: number;
}

export function reviewSummary(
  cards: DwbCard[] | null | undefined,
): DwbReviewSummary {
  const list = Array.isArray(cards) ? cards : [];
  return list.reduce<DwbReviewSummary>(
    (summary, card) => {
      if (card?.review_status === "approved") summary.approved += 1;
      else if (card?.review_status === "rejected") summary.rejected += 1;
      else summary.pending += 1;
      summary.total += 1;
      return summary;
    },
    { total: 0, pending: 0, approved: 0, rejected: 0 },
  );
}

function requiredString(value: unknown, label: string): string {
  if (typeof value !== "string" || !value.trim()) {
    throw new Error(`${label} ontbreekt in het selectie-artifact.`);
  }
  return value.trim();
}

function asObject(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new Error("Het selectie-artifact moet één JSON-object bevatten.");
  }
  return value as Record<string, unknown>;
}

export interface ParsedDwbSelection {
  selectorVersion: string;
  selectorCommit: string;
  selectionSeed: string;
  cards: DwbSelectionCardInput[];
}

export function parseDwbSelectionArtifact(
  text: string,
  expectedWeek: string,
): ParsedDwbSelection {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text) as unknown;
  } catch {
    throw new Error("Het geselecteerde bestand is geen geldige JSON-selectie.");
  }
  const payload = asObject(parsed);
  if (payload.schema_version !== 1) {
    throw new Error("Alleen DWB selectie schema_version 1 wordt ondersteund.");
  }
  if (payload.week !== expectedWeek) {
    throw new Error(
      `Selectie hoort bij ${
        typeof payload.week === "string" ? payload.week : "een andere week"
      }, niet bij ${expectedWeek}.`,
    );
  }

  const selector = asObject(payload.selector);
  if (selector.version !== DWB_SELECTOR_VERSION) {
    throw new Error(
      `Onbekende selectorversie: ${String(selector.version ?? "ontbreekt")}.`,
    );
  }
  if (selector.repository !== DWB_SELECTOR_REPOSITORY) {
    throw new Error(
      "Selectie is niet afkomstig uit de canonieke dbvdw-dwb repo.",
    );
  }
  const selectorCommit = requiredString(selector.commit, "selector.commit")
    .toLowerCase();
  if (!COMMIT_RE.test(selectorCommit)) {
    throw new Error(
      "selector.commit moet een exacte 40-char Git commit zijn; lokale ongepinde output wordt niet geïmporteerd.",
    );
  }

  const selectionSeed = requiredString(
    payload.selection_seed_decimal,
    "selection_seed_decimal",
  );
  if (!SEED_RE.test(selectionSeed)) {
    throw new Error(
      "selection_seed_decimal moet een unsigned decimale 64-bit seed bevatten.",
    );
  }
  try {
    const value = BigInt(selectionSeed);
    if (value < 0n || value > 18_446_744_073_709_551_615n) {
      throw new Error("buiten bereik");
    }
  } catch {
    throw new Error(
      "selection_seed_decimal valt buiten het unsigned 64-bit bereik.",
    );
  }

  if (!Array.isArray(payload.cards) || payload.cards.length === 0) {
    throw new Error("Het selectie-artifact bevat geen kaarten.");
  }

  const cards = payload.cards.map((raw, index) => {
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
      throw new Error(`Kaart ${index + 1} is geen geldig object.`);
    }
    const card = raw as Record<string, unknown>;
    if (card.week !== expectedWeek) {
      throw new Error(`Kaart ${index + 1} hoort niet bij ${expectedWeek}.`);
    }
    const projectEmployeeId = requiredString(
      card.project_employee_id,
      `kaart ${index + 1} project_employee_id`,
    );
    if (!UUID_RE.test(projectEmployeeId)) {
      throw new Error(
        `Kaart ${index + 1} heeft geen geldige project_employee_id.`,
      );
    }
    const normalized: DwbSelectionCardInput = {
      ...card,
      week: expectedWeek,
      project_employee_id: projectEmployeeId,
      naam: requiredString(card.naam, `kaart ${index + 1} naam`),
      datum: requiredString(card.datum, `kaart ${index + 1} datum`),
      entry_id: requiredString(card.entry_id, `kaart ${index + 1} entry_id`),
      activity_type: requiredString(
        card.activity_type,
        `kaart ${index + 1} activity_type`,
      ),
      type_observatie: requiredString(
        card.type_observatie,
        `kaart ${index + 1} type_observatie`,
      ),
      categorie: requiredString(card.categorie, `kaart ${index + 1} categorie`),
      omschrijving: requiredString(
        card.omschrijving,
        `kaart ${index + 1} omschrijving`,
      ),
    };
    return normalized;
  });

  return {
    selectorVersion: DWB_SELECTOR_VERSION,
    selectorCommit,
    selectionSeed,
    cards,
  };
}
